import type { App } from './App';
import { button } from '../ui/components';
import { h } from '../ui/dom';
import { t } from '../i18n';
import { APP_VERSION } from './version';
import { rememberScene } from './resume';

/** Build id baked in by vite.config.ts ('dev' under the dev server). */
declare const __BUILD_ID__: string;

const CHECK_EVERY_MS = 5 * 60_000;
const SEEN_KEY = 'paper-legends/seen-version';
/** Screens where reloading cannot interrupt a fight or a drawing. */
const SAFE_SCENES = new Set(['menu', 'notebook', 'help', 'multiplayer']);

/**
 * Notices when a new version was deployed while this tab stayed open (phones
 * keep tabs for days). On a calm screen the game saves and reloads itself;
 * otherwise a note offers the update, and it is applied at the next calm moment.
 */
export function watchForUpdates(app: App): void {
  if (__BUILD_ID__ === 'dev') return;
  announceNewVersion(app);
  let pending = false;
  let note: HTMLElement | null = null;
  let nextVersion = '';

  const reload = async (): Promise<void> => {
    if (app.scenes.currentName) rememberScene(app.scenes.currentName, app.scenes.currentParams);
    app.store.flush();
    await Promise.race([app.cloud.syncNow(), new Promise((r) => setTimeout(r, 2500))]);
    location.reload();
  };

  const calm = (): boolean => SAFE_SCENES.has(app.scenes.currentName ?? '') && !document.querySelector('.modal-wrap') && app.room.room?.status !== 'battle';

  const offer = (): void => {
    if (note) return;
    note = h(
      'div',
      { class: 'update-note sticky-note yellow', role: 'status' },
      h('span', null, nextVersion ? t('Paper Legends v{version} is ready.', { version: nextVersion }) : t('A new version of Paper Legends is ready.')),
      button(t('Update now'), { small: true, variant: 'primary', color: 'yellow', onClick: () => void reload() }),
    );
    document.getElementById('overlay-layer')?.appendChild(note);
  };

  const check = async (): Promise<void> => {
    if (pending) {
      if (calm() && document.visibilityState === 'visible') await reload();
      return;
    }
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const { build, version } = (await res.json()) as { build?: string; version?: string };
      if (!build || build === __BUILD_ID__) return;
      nextVersion = version ?? '';
    } catch {
      return; // offline: try again later
    }
    pending = true;
    if (calm()) await reload();
    else offer();
  };

  window.setInterval(() => void check(), CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check();
  });
  // Leaving a battle or the creator is a calm moment to apply a waiting update.
  window.addEventListener('hashchange', () => {
    if (pending) window.setTimeout(() => void check(), 500);
  });
  void check();
}

/** Once per new version: a short note that the game was updated (not on a first visit). */
function announceNewVersion(app: App): void {
  try {
    const seen = localStorage.getItem(SEEN_KEY);
    if (seen !== APP_VERSION) {
      localStorage.setItem(SEEN_KEY, APP_VERSION);
      if (seen) app.toast(t('Updated to v{version}!', { version: APP_VERSION }), 'success');
    }
  } catch {
    /* private mode: no note */
  }
}
